import React, { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Send, CheckCircle2, Clock, MessageSquare, RotateCcw, ExternalLink, AlertCircle, Loader2, FileText, Inbox, Sparkles, Plus, Trash2 } from "lucide-react";
import Layout, { PageHeader, StatCard } from "@/components/Layout";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";
import { useSubmitting } from "@/lib/useSubmitting";

const STATUS_META = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-600 border-slate-200", Icon: FileText },
  submitted_to_head: { label: "Pending Review", color: "bg-amber-100 text-amber-700 border-amber-200", Icon: Clock },
  approved_to_seo: { label: "With SEO", color: "bg-sky-100 text-sky-700 border-sky-200", Icon: Send },
  posted: { label: "Posted ✓", color: "bg-emerald-100 text-emerald-700 border-emerald-200", Icon: CheckCircle2 },
};

export default function BlogWorkflowPage() {
  const { user, customRole } = useAuth();
  const role = user.role;
  const [rows, setRows] = useState([]);
  const [docs, setDocs] = useState([]); // user's own blogs (for executives' "submit" panel)
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [blog, setBlog] = useState(null);
  const [reassignNote, setReassignNote] = useState("");
  const [newRemark, setNewRemark] = useState("");
  const submit = useSubmitting();
  const approve = useSubmitting();
  const reassign = useSubmitting();
  const submitOwn = useSubmitting();
  const remark = useSubmitting();

  const isExec = role === "media_comm_executive";
  const isHead = role === "head_media_comm";
  const isAdmin = role === "admin";
  const isSeo = role === "seo";
  const canRemark = isAdmin || isHead || isExec || isSeo;

  const load = async () => {
    setLoading(true);
    try {
      const [wf, ds] = await Promise.all([
        api.get("/blog-workflow"),
        (isExec || isHead || isAdmin) ? api.get("/documents", { params: { kind: "blog" } }) : Promise.resolve({ data: [] }),
      ]);
      setRows(wf.data || []);
      setDocs(ds.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Blogs the user owns that are NOT already submitted/approved/posted → can be submitted
  const wfByDocId = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.doc_id] = r; });
    return m;
  }, [rows]);
  const submittableOwnBlogs = docs.filter((d) => {
    const wf = wfByDocId[d.doc_id];
    return !wf || wf.status === "draft";
  });

  const openRow = async (row) => {
    setOpen(row); setBlog(null); setReassignNote(""); setNewRemark("");
    try {
      const r = await api.get(`/blog-workflow/${row.doc_id}/blog`);
      setBlog(r.data);
    } catch (e) {
      toast.error("Couldn't load blog");
    }
  };

  const doAddRemark = () =>
    remark.run(async () => {
      const text = newRemark.trim();
      if (!text) { toast.error("Remark cannot be empty"); return; }
      try {
        const r = await api.post(`/blog-workflow/${open.doc_id}/comments`, { text });
        toast.success("Remark added");
        setNewRemark("");
        // Patch the open modal + the row list in-place so the new remark
        // shows immediately without a full reload.
        setOpen((cur) => (cur && cur.doc_id === r.data.doc_id ? { ...cur, ...r.data } : cur));
        setRows((rs) => rs.map((x) => (x.doc_id === r.data.doc_id ? { ...x, ...r.data } : x)));
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not add remark");
      }
    });

  const doSubmitOwn = (doc) =>
    submitOwn.run(async () => {
      try {
        await api.post(`/blog-workflow/${doc.doc_id}/submit-to-head`);
        toast.success("Submitted to Head of Media for review");
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not submit");
      }
    });

  const adminDelete = async (row, e) => {
    e.stopPropagation();
    if (!isAdmin) return;
    const ok = window.confirm(`Permanently delete the blog "${row.title || "Untitled"}"?\n\nThis removes it from EVERYONE's dashboard (author, Head, SEO). Cannot be undone.`);
    if (!ok) return;
    try {
      await api.delete(`/blog-workflow/${row.doc_id}`);
      toast.success("Blog deleted");
      setOpen((cur) => (cur && cur.doc_id === row.doc_id ? null : cur));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const doApprove = () =>
    approve.run(async () => {
      try {
        await api.post(`/blog-workflow/${open.doc_id}/approve-to-seo`);
        toast.success("Approved — now visible to SEO");
        setOpen(null);
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not approve");
      }
    });

  const doReassign = () =>
    reassign.run(async () => {
      if (!reassignNote.trim()) { toast.error("Add a note explaining what to fix"); return; }
      try {
        await api.post(`/blog-workflow/${open.doc_id}/reassign`, { note: reassignNote.trim() });
        toast.success("Sent back to author with your note");
        setOpen(null);
        load();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not reassign");
      }
    });

  // Bucket views per role
  const bucketed = useMemo(() => {
    if (isHead || isAdmin) {
      return {
        inbox: rows.filter((r) => r.status === "submitted_to_head"),
        sent_back: rows.filter((r) => r.status === "draft" && (r.head_notes || "").trim().length > 0),
        with_seo: rows.filter((r) => r.status === "approved_to_seo"),
        posted: rows.filter((r) => r.status === "posted"),
      };
    }
    if (isExec) {
      return {
        reassigned: rows.filter((r) => r.status === "draft" && (r.head_notes || "").trim().length > 0),
        pending: rows.filter((r) => r.status === "submitted_to_head"),
        with_seo: rows.filter((r) => r.status === "approved_to_seo"),
        posted: rows.filter((r) => r.status === "posted"),
      };
    }
    return { all: rows };
  }, [rows, isHead, isAdmin, isExec]);

  const totals = useMemo(() => ({
    submitted: rows.filter((r) => r.status === "submitted_to_head").length,
    sent_back: rows.filter((r) => r.status === "draft" && (r.head_notes || "").trim().length > 0).length,
    with_seo: rows.filter((r) => r.status === "approved_to_seo").length,
    posted: rows.filter((r) => r.status === "posted").length,
  }), [rows]);

  return (
    <Layout navItems={getNav(role, customRole)}>
      <div className="p-6 lg:p-10 max-w-6xl mx-auto">
        <PageHeader
          title="Blog Publication Workflow"
          subtitle={isHead ? "Review submissions from your team, approve to SEO, or send back for changes." : isExec ? "Submit your finished drafts to Head of Media for review." : "End-to-end blog pipeline visibility."}
          icon={Sparkles}
          accent="amber"
        />

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatCard label="Pending Head Review" value={totals.submitted} icon={Clock} color="amber" />
          <StatCard label="Sent Back" value={totals.sent_back} icon={RotateCcw} color="rose" />
          <StatCard label="With SEO" value={totals.with_seo} icon={Send} color="sky" />
          <StatCard label="Posted" value={totals.posted} icon={CheckCircle2} color="emerald" />
        </div>

        {/* Executive: own blogs ready to submit */}
        {isExec && submittableOwnBlogs.length > 0 && (
          <section className="dashboard-card p-5 mb-6">
            <h3 className="font-display font-bold text-lg mb-3">Ready to submit ({submittableOwnBlogs.length})</h3>
            <div className="divide-y divide-slate-100">
              {submittableOwnBlogs.map((d) => (
                <div key={d.doc_id} className="py-3 flex items-center gap-3" data-testid={`submit-row-${d.doc_id}`}>
                  <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{d.title || "Untitled blog"}</div>
                    <div className="text-xs text-slate-500">Last edited {d.last_edited_at ? new Date(d.last_edited_at).toLocaleString() : "—"}</div>
                  </div>
                  <button onClick={() => doSubmitOwn(d)} disabled={submitOwn.submitting} className="btn-primary px-4 py-2 text-xs disabled:opacity-50 inline-flex items-center gap-1" data-testid={`submit-btn-${d.doc_id}`}>
                    {submitOwn.submitting ? <><Loader2 className="w-3 h-3 animate-spin" /> Sending…</> : <><Send className="w-3 h-3" /> Submit to Head</>}
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Head / Admin: inbox */}
        {(isHead || isAdmin) && (
          <Bucket title={`Inbox · Awaiting your review (${bucketed.inbox.length})`} rows={bucketed.inbox} onOpen={openRow} accent="amber" emptyText="No blogs waiting on you." canDelete={isAdmin} onDelete={adminDelete} />
        )}

        {/* Head / Admin: sent-back — blogs pushed back to authors with a head note. Admin sees the review thread. */}
        {(isHead || isAdmin) && (
          <Bucket
            title={`Sent Back to Authors (${(bucketed.sent_back || []).length})`}
            rows={bucketed.sent_back}
            onOpen={openRow}
            accent="rose"
            emptyText="No blogs currently sent back."
            canDelete={isAdmin}
            onDelete={adminDelete}
          />
        )}

        {(isHead || isAdmin || isExec) && (
          <Bucket title={`With SEO (${(bucketed.with_seo || []).length})`} rows={bucketed.with_seo} onOpen={openRow} accent="sky" canDelete={isAdmin} onDelete={adminDelete} />
        )}
        {(isHead || isAdmin || isExec) && (
          <Bucket title={`Posted (${(bucketed.posted || []).length})`} rows={bucketed.posted} onOpen={openRow} accent="emerald" canDelete={isAdmin} onDelete={adminDelete} />
        )}

        {/* Executive: reassigned (Head sent it back) */}
        {isExec && (bucketed.reassigned || []).length > 0 && (
          <Bucket title="Sent back to you" rows={bucketed.reassigned} onOpen={openRow} accent="rose" />
        )}

        {loading && <div className="py-8 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>}
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
            <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }} className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col relative">
              <ModalCloseBtn onClose={() => { setOpen(null); setBlog(null); }} />
              <div className="p-6 border-b border-slate-100">
                <div className="flex items-start gap-3 pr-10">
                  <div className="flex-1">
                    <div className="text-xs text-slate-500 uppercase tracking-wide font-bold mb-1">{open.author_name} · {open.author_role}</div>
                    <h3 className="font-display font-black text-2xl">{open.title || "Untitled blog"}</h3>
                  </div>
                  <div className={`px-3 py-1.5 rounded-full text-xs font-bold border ${(STATUS_META[open.status] || {}).color || "bg-slate-100"} shrink-0`}>
                    {(STATUS_META[open.status] || {}).label || open.status}
                  </div>
                </div>
                {open.head_notes && open.status === "draft" && (
                  <div className="mt-3 bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-800">
                    <div className="font-bold mb-1 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Head&apos;s note:</div>
                    {open.head_notes}
                  </div>
                )}
                {open.posted_url && (
                  <a href={open.posted_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1 mt-2"><ExternalLink className="w-3 h-3" /> {open.posted_url}</a>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-6 grid lg:grid-cols-5 gap-6">
                <div className="lg:col-span-3 space-y-3">
                  {!blog && (
                    <div className="text-slate-400 py-12 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
                  )}
                  {blog && (() => {
                    const d = blog.doc || {};
                    const gdocUrl = d.gdoc_web_url || (d.gdoc_id ? `https://docs.google.com/document/d/${d.gdoc_id}/edit` : null);
                    const previewUrl = d.gdoc_id ? `https://docs.google.com/document/d/${d.gdoc_id}/preview` : null;
                    const html = d.content_html || "";
                    const htmlEmpty = !html.replace(/<[^>]+>/g, "").trim();

                    if (gdocUrl) {
                      // Google-Docs-backed blog → iframe preview + Open button.
                      return (
                        <>
                          <div className="flex items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2">
                            <div className="text-xs text-sky-900 flex items-center gap-2 min-w-0">
                              <FileText className="w-4 h-4 shrink-0" />
                              <span className="truncate">Google Doc — live source</span>
                            </div>
                            <a
                              href={gdocUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1 shrink-0"
                              data-testid="open-gdoc-btn"
                            >
                              <ExternalLink className="w-3 h-3" /> Open in Google Docs
                            </a>
                          </div>
                          <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" style={{ minHeight: 500 }}>
                            <iframe
                              src={previewUrl}
                              title={d.title || "Blog preview"}
                              className="w-full"
                              style={{ height: 560, border: "none" }}
                              data-testid="gdoc-preview-iframe"
                            />
                          </div>
                          <p className="text-[11px] text-slate-400 leading-relaxed">
                            Preview not loading? It means the doc isn&apos;t shared with your Google account yet — click <b>Open in Google Docs</b> above and request access from the author.
                          </p>
                        </>
                      );
                    }

                    // Legacy HTML-backed blog → render inline
                    return (
                      <div className="prose prose-sm max-w-none">
                        <div dangerouslySetInnerHTML={{ __html: htmlEmpty ? "<p><em>This blog has no content yet.</em></p>" : html }} />
                      </div>
                    );
                  })()}
                </div>
                <div className="lg:col-span-2 space-y-4">
                  {/* Remarks thread — admin / head / exec / SEO can add, authors see */}
                  <div className="border border-slate-200 rounded-xl p-4" data-testid="blog-remarks-panel">
                    <div className="text-xs uppercase font-bold text-slate-500 mb-2 flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> Remarks
                      {(open.seo_comments || []).length > 0 && (
                        <span className="ml-1 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-700">{open.seo_comments.length}</span>
                      )}
                    </div>
                    {(open.seo_comments || []).length === 0 ? (
                      <div className="text-xs text-slate-400 italic">No remarks yet.</div>
                    ) : (
                      <div className="space-y-2 max-h-56 overflow-y-auto pr-1 mb-2">
                        {open.seo_comments.map((c, i) => (
                          <div key={i} className={`rounded-lg p-2 border ${c.role === "admin" ? "bg-orange-50 border-orange-200" : c.role === "seo" ? "bg-lime-50 border-lime-100" : "bg-sky-50 border-sky-100"}`} data-testid={`blog-remark-${i}`}>
                            <div className="flex items-center justify-between gap-2">
                              <div className="text-xs font-bold text-slate-700">{c.name} <span className="font-normal text-slate-500">· {c.role_label || c.role}</span></div>
                              <div className="text-[10px] text-slate-400">{new Date(c.at).toLocaleString()}</div>
                            </div>
                            <div className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{c.text}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {canRemark && open.status !== "posted" && (
                      <div className="border-t border-slate-100 pt-3">
                        <textarea
                          rows={2}
                          className="input-pill text-sm w-full mb-2"
                          placeholder={isAdmin ? "Leave a remark for the author / team…" : "Add a remark…"}
                          value={newRemark}
                          onChange={(e) => setNewRemark(e.target.value)}
                          data-testid="blog-remark-input"
                        />
                        <button
                          onClick={doAddRemark}
                          disabled={remark.submitting || !newRemark.trim()}
                          className="btn-primary w-full py-2 text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
                          data-testid="blog-remark-submit"
                        >
                          {remark.submitting ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Posting…</> : <><Plus className="w-3.5 h-3.5" /> Post remark</>}
                        </button>
                      </div>
                    )}
                    {open.status === "posted" && (
                      <div className="text-xs text-slate-400 italic mt-2">Blog is posted — remarks are locked.</div>
                    )}
                  </div>

                  {/* Head/Admin actions */}
                  {(isHead || isAdmin) && open.status === "submitted_to_head" && (
                    <div className="border border-emerald-200 bg-emerald-50/40 rounded-xl p-4">
                      <div className="text-xs uppercase font-bold text-emerald-800 mb-2">Decision</div>
                      <button onClick={doApprove} disabled={approve.submitting} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold w-full py-2 rounded-full text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2 mb-2" data-testid="wf-approve">
                        {approve.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Approving…</> : <><CheckCircle2 className="w-4 h-4" /> Approve → Send to SEO</>}
                      </button>
                      <div className="border-t border-emerald-200 my-3" />
                      <div className="text-xs uppercase font-bold text-rose-800 mb-2">…or reassign back to author</div>
                      <textarea rows={3} className="input-pill text-sm w-full mb-2" placeholder="What needs fixing? (required)" value={reassignNote} onChange={(e) => setReassignNote(e.target.value)} data-testid="wf-reassign-note" />
                      <button onClick={doReassign} disabled={reassign.submitting || !reassignNote.trim()} className="bg-rose-600 hover:bg-rose-700 text-white font-bold w-full py-2 rounded-full text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="wf-reassign">
                        {reassign.submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><RotateCcw className="w-4 h-4" /> Send back with note</>}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
}

function Bucket({ title, rows, onOpen, accent = "slate", emptyText, canDelete = false, onDelete }) {
  if (!rows || rows.length === 0) {
    if (emptyText) return (
      <section className="dashboard-card p-5 mb-6">
        <h3 className="font-display font-bold text-lg mb-3">{title}</h3>
        <div className="text-center py-6 text-slate-400 text-sm"><Inbox className="w-8 h-8 mx-auto mb-1" /> {emptyText}</div>
      </section>
    );
    return null;
  }
  return (
    <section className="dashboard-card p-5 mb-6">
      <h3 className="font-display font-bold text-lg mb-3">{title}</h3>
      <div className="divide-y divide-slate-100">
        {rows.map((row) => {
          const meta = STATUS_META[row.status] || { label: row.status, color: "bg-slate-100 text-slate-600 border-slate-200", Icon: AlertCircle };
          const StatusIcon = meta.Icon;
          return (
            <div key={row.doc_id} className="flex items-center gap-1 group" data-testid={`wf-row-${row.doc_id}`}>
              <button onClick={() => onOpen(row)} className="flex-1 text-left py-3 px-2 hover:bg-slate-50 transition flex items-center gap-3 rounded-lg">
                <div className={`px-2.5 py-1 rounded-full text-xs font-bold border ${meta.color} flex items-center gap-1 shrink-0`}>
                  <StatusIcon className="w-3 h-3" /> {meta.label}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-800 truncate">{row.title}</div>
                  <div className="text-xs text-slate-500">By {row.author_name || "—"} · {row.last_action_at ? new Date(row.last_action_at).toLocaleDateString() : "—"}</div>
                </div>
                {(row.seo_comments || []).length > 0 && (
                  <span className="text-xs text-pink-600 flex items-center gap-1 shrink-0"><MessageSquare className="w-3 h-3" /> {row.seo_comments.length}</span>
                )}
              </button>
              {canDelete && onDelete && (
                <button
                  onClick={(e) => onDelete(row, e)}
                  className="p-2 rounded-lg text-slate-300 hover:text-rose-600 hover:bg-rose-50 transition opacity-60 group-hover:opacity-100 shrink-0"
                  title="Admin: delete blog from workflow"
                  data-testid={`wf-row-delete-${row.doc_id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
